import { listLibrary, listProgress, putProgress } from '@/lib/api/api-client';
import {
  journeySummaryFrom,
  readJourneyProgress,
  toJourneyProgress,
} from '@/lib/library/journey-glance';
import { journeyStorageKey, type StorageLike } from './progress-store';
import type { Repertoire } from './repertoire';

/**
 * Combien de morceaux récemment ouverts on inspecte pour retrouver le dernier
 * travaillé. Une page de « Mes morceaux ».
 */
const LAST_OPENED_LOOKBACK = 30;

/**
 * Marque que le rattrapage des résumés a été fait dans ce navigateur.
 *
 * **Par navigateur et non par compte**, parce que c'est ce qui est rattrapé :
 * les documents de parcours ne sont pas rangés par utilisateur dans
 * `localStorage` (`virtuosa:journey:<morceauId>`), et la passe pousse ce que
 * *cet appareil* connaît. Un marqueur par compte relancerait la même passe sur
 * les mêmes documents à chaque changement de compte.
 *
 * Hors du préfixe `virtuosa:journey:` pour ne pas se retrouver énuméré comme un
 * parcours par la passe elle-même. Effacer cette clé la rejoue, ce qui est la
 * façon de la relancer si le format du résumé change un jour.
 */
const SUMMARY_BACKFILL_KEY = 'virtuosa:progress-summary-backfill';

/**
 * Le stockage, plus de quoi l'énumérer.
 *
 * `StorageLike` ne sait que lire une clé connue, ce qui suffit à tout le reste
 * du parcours. Le rattrapage, lui, doit répondre à « quels parcours ce
 * navigateur connaît-il ? », et c'est la seule question du produit qui demande
 * de parcourir le stockage. Un type à part plutôt qu'un élargissement de
 * `StorageLike` : les magasins en mémoire des tests n'ont pas à grandir pour
 * une passe qu'ils n'exercent pas.
 *
 * `window.localStorage` le satisfait tel quel.
 */
export interface EnumerableStorage extends StorageLike {
  readonly length: number;
  key(index: number): string | null;
}

/**
 * Redescend la progression du compte dans le navigateur.
 *
 * C'est la moitié qui rend le compte utile à qui joue : `SyncingProgressStore`
 * pousse vers le serveur, celle-ci ramène. Sans elle, ouvrir Virtuosa sur un
 * autre écran repart de zéro.
 *
 * # Elle ne remplit que les trous
 *
 * Un morceau déjà connu localement n'est **jamais** écrasé, et ce n'est pas de
 * la prudence gratuite : le document `JourneyProgress` ne porte pas de date. On
 * ne peut donc pas savoir laquelle des deux copies est la plus récente, et
 * écraser reviendrait à parier — un pari qui, perdu, efface une session de
 * travail. Ne remplir que ce qui manque ne peut, au pire, que retarder une
 * synchronisation : la prochaine sauvegarde locale repoussera vers le serveur.
 *
 * Conséquence à connaître : deux appareils qui avancent chacun de leur côté ne
 * convergent pas. Le dernier qui sauvegarde gagne côté serveur, et chacun garde
 * sa copie locale. C'est le comportement d'un coffre, pas d'une synchronisation
 * — et c'est ce qu'on veut tant que le serveur ignore ce qu'est une phrase.
 *
 * # Elle n'échoue jamais bruyamment
 *
 * Appelée juste après l'ouverture de session, en même temps que la revendication
 * des imports. Un réseau qui lâche à ce moment-là ne doit pas empêcher
 * d'entrer : on repart simplement sans la progression distante, et la prochaine
 * ouverture réessaiera.
 *
 * @returns le nombre de morceaux effectivement redescendus
 */
export async function hydrateProgressFromServer(
  storage: StorageLike,
): Promise<number> {
  let hydrated = 0;

  try {
    const { items } = await listProgress();

    for (const item of items) {
      const key = journeyStorageKey(item.morceauId);

      if (storage.getItem(key) !== null) {
        continue;
      }

      storage.setItem(key, JSON.stringify(item.data));
      hydrated++;
    }
  } catch {
    // Voir l'en-tête : silence délibéré.
  }

  return hydrated;
}

/**
 * Retrouve, depuis le compte, le morceau que le joueur travaillait.
 *
 * # Pourquoi ça ne demande aucun endpoint
 *
 * Le pointeur local (`virtuosa:working`) ne se synchronise pas, mais le fait
 * qu'il désigne, lui, est déjà sur le serveur deux fois : « Mes morceaux »
 * range les entrées par dernière ouverture, et `journey_progress` porte la
 * progression de chacune. Un morceau ouvert récemment **et** qui a un
 * parcours rangé, c'est la définition du dernier entraînement — reconstituée,
 * pas stockée une troisième fois.
 *
 * # Après `hydrateProgressFromServer`, jamais avant
 *
 * C'est elle qui redescend les documents de parcours, et c'est leur présence
 * qui distingue un morceau travaillé d'un morceau simplement écouté. Appelée
 * dans l'autre ordre sur un appareil neuf, la boucle ci-dessous ne trouverait
 * aucun parcours et ne proposerait rien.
 *
 * # Bornée à une page
 *
 * On ne remonte pas toute la bibliothèque pour trouver un parcours : au-delà
 * des trente derniers morceaux ouverts, « le dernier entraînement » n'est plus
 * une reprise, c'est de l'archéologie. Rien plutôt qu'une offre pour un
 * morceau oublié depuis deux ans.
 *
 * Silencieuse comme sa voisine : une panne ici coûte une ligne sur un écran,
 * pas une entrée dans l'application.
 *
 * @returns `true` si un morceau a été retenu
 */
export async function seedWorkingPieceFromServer(
  storage: StorageLike,
  into: Repertoire,
): Promise<boolean> {
  try {
    const { items } = await listLibrary({ limit: LAST_OPENED_LOOKBACK });

    for (const entry of items) {
      // `openable` faux, c'est un fichier sans version jouable : la ligne resterait
      // cliquable et ne mènerait nulle part.
      if (!entry.openable || entry.midiFileId === null) {
        continue;
      }

      if (readJourneyProgress(storage, entry.midiFileId) === null) {
        continue;
      }

      into.seedWorking(
        {
          morceauId: entry.midiFileId,
          pieceId: entry.pieceId,
          title: entry.title,
        },
        Date.parse(entry.lastOpenedAt),
      );

      return true;
    }
  } catch {
    // Voir l'en-tête : silence délibéré.
  }

  return false;
}

/**
 * Rattrape les résumés de progression manquants, une fois par navigateur.
 *
 * # Pourquoi une passe, et pourquoi côté client
 *
 * Le résumé est né avec « Là où tu t'es arrêté » : c'est lui que le serveur
 * indexe pour répondre à `filter=in-progress`. Tous les documents sauvegardés
 * avant lui en sont dépourvus, et le serveur ne peut pas les rattraper seul —
 * il ne sait pas ce qu'est une phrase, c'est tout l'intérêt du coffre. Sans
 * cette passe, l'écran serait vide pour chaque compte existant jusqu'à ce que
 * son propriétaire rejoue chacun de ses morceaux.
 *
 * # Elle ne peut pas écraser un travail plus récent
 *
 * `PUT /api/progress` remplace le document entier, donc pousser une copie
 * locale périmée effacerait une session faite ailleurs — le contraire exact de
 * ce que `hydrateProgressFromServer` s'interdit deux fonctions plus haut. La
 * passe se tient donc à deux cas où la question ne se pose pas :
 *
 *  1. le serveur a le document mais pas son résumé : on lui repousse **ses
 *     propres données**, inchangées, avec le résumé qu'on en déduit ;
 *  2. le serveur ne connaît pas le morceau : il n'y a rien à écraser. C'est le
 *     cas de qui a joué sans compte avant d'en créer un.
 *
 * Un document connu des deux côtés mais divergent n'est pas touché : sa
 * prochaine sauvegarde le poussera, résumé compris.
 *
 * # Une seule fois, marquée dans le navigateur
 *
 * Voir `SUMMARY_BACKFILL_KEY`. Le marqueur n'est posé qu'une fois **toute** la
 * passe réussie : un envoi qui échoue la laisse à rejouer à la session
 * suivante, ce qui est sans conséquence puisque réécrire le même document avec
 * le même résumé est idempotent.
 *
 * Silencieuse comme ses voisines, et à appeler **après**
 * `hydrateProgressFromServer` : c'est elle qui redescend les documents dont on
 * résume ensuite ceux que le serveur avait déjà.
 *
 * @returns le nombre de documents effectivement repoussés
 */
export async function backfillProgressSummaries(
  storage: EnumerableStorage,
): Promise<number> {
  if (alreadyBackfilled(storage)) {
    return 0;
  }

  let pushed = 0;

  try {
    const { items } = await listProgress();
    const onServer = new Set(items.map((item) => item.morceauId));

    // Séquentiel et non en parallèle : la passe part au moment où la session
    // s'ouvre, et une rafale de PUT y disputerait la bande passante aux appels
    // dont dépend le premier écran.
    for (const item of items) {
      if (item.summary !== null) {
        continue;
      }

      const remote = toJourneyProgress(item.data);
      const summary = remote === null ? null : journeySummaryFrom(remote);

      if (summary === null) {
        continue;
      }

      await putProgress(item.morceauId, item.schemaVersion, item.data, summary);
      pushed++;
    }

    for (const morceauId of localJourneyIds(storage)) {
      if (onServer.has(morceauId)) {
        continue;
      }

      const progress = readJourneyProgress(storage, morceauId);

      if (progress === null) {
        continue;
      }

      const summary = journeySummaryFrom(progress);

      if (summary === null) {
        continue;
      }

      await putProgress(morceauId, progress.schemaVersion, progress, summary);
      pushed++;
    }

    storage.setItem(SUMMARY_BACKFILL_KEY, new Date().toISOString());
  } catch {
    // Voir l'en-tête : silence délibéré, et pas de marqueur, donc rejouée.
  }

  return pushed;
}

function alreadyBackfilled(storage: EnumerableStorage): boolean {
  try {
    return storage.getItem(SUMMARY_BACKFILL_KEY) !== null;
  } catch {
    // Stockage refusé (mode privé) : on ne saurait pas non plus mémoriser que
    // la passe a tourné, donc la rejouer à chaque session serait une requête
    // par morceau à chaque ouverture. On s'abstient.
    return true;
  }
}

/**
 * Les `morceauId` dont ce navigateur porte un parcours.
 *
 * Le préfixe est dérivé de `journeyStorageKey` plutôt que recopié : une
 * seconde écriture littérale du préfixe serait la première à devenir fausse.
 *
 * Les clés transposées (`<midiFileId>:t<n>`) sont écartées : `PUT
 * /api/progress/{morceauId}` n'accepte que `[0-9a-zA-Z-]`, elles n'ont donc
 * jamais atteint le serveur et ne l'atteindront pas davantage ici. C'est
 * cohérent avec ce que `journey-glance.ts` assume déjà — une tonalité
 * différente est un autre parcours, qui ne se montre pas sous la clé de base.
 *
 * Le préfixe abrite aussi des clés qui ne sont pas des parcours (le journal de
 * run, l'état du calque de débogage) ; la garde de forme de
 * `readJourneyProgress` les écarte à la lecture.
 */
function localJourneyIds(storage: EnumerableStorage): string[] {
  const prefix = journeyStorageKey('');
  const ids: string[] = [];

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);

    if (key === null || !key.startsWith(prefix)) {
      continue;
    }

    const morceauId = key.slice(prefix.length);

    if (morceauId === '' || morceauId.includes(':')) {
      continue;
    }

    ids.push(morceauId);
  }

  return ids;
}
